import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasEssentialConsent, setCookieConsent } from '../utils/cookieConsent';

const PUBLIC_PATHS = new Set([
  '/privacy-policy',
  '/cookie-policy',
  '/terms-of-service',
  // Auth routes must remain reachable for public registration.
  '/signin',
  '/signup',
  '/forgot-password',
  '/confirm-account',
]);

const CookieConsentGate = ({ children }) => {
  const location = useLocation();
  const [accepted, setAccepted] = useState(() => hasEssentialConsent());

  const isPublicPath = useMemo(() => {
    try {
      return PUBLIC_PATHS.has(location.pathname);
    } catch {
      return false;
    }
  }, [location.pathname]);

  useEffect(() => {
    setAccepted(hasEssentialConsent());
  }, [location.pathname]);

  if (accepted || isPublicPath) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white px-4">
      <div className="w-full max-w-xl rounded-lg border border-gray-700 bg-gray-800 p-6 shadow-lg">
        <h1 className="text-2xl font-bold">Cookie & Storage Consent Required</h1>
        <p className="mt-3 text-sm text-gray-300">
          BorsAI requires essential cookies and/or browser storage to create your account, keep you signed in, and secure your session.
          Without this, we can’t provide access to the platform.
        </p>

        <div className="mt-4 text-sm text-gray-300">
          <div className="font-semibold text-gray-200">We use essential storage for:</div>
          <ul className="mt-2 list-disc list-inside space-y-1">
            <li>Saving your login session (authentication token/session identifier)</li>
            <li>Account security (fraud prevention, CSRF protection)</li>
            <li>Core platform functionality</li>
          </ul>
        </div>

        <div className="mt-4 text-xs text-gray-400">
          Read more: <Link className="underline" to="/cookie-policy">Cookie Policy</Link> •{' '}
          <Link className="underline" to="/privacy-policy">Privacy Policy</Link> •{' '}
          <Link className="underline" to="/terms-of-service">Terms of Service</Link>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center justify-center">
          <button
            type="button"
            className="inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            onClick={() => {
              const consent = { essential: true, analytics: false, preferences: true, acceptedAt: new Date().toISOString() };
              setCookieConsent(consent);
              // Do NOT navigate: the gate renders in place, so flipping
              // `accepted` re-renders `children` at the current URL.
              // Navigating (e.g. to `location.state.from` or '/') would yank
              // users off the page they actually requested.
              setAccepted(true);
            }}
          >
            I Agree (Required)
          </button>

          <button
            type="button"
            className="inline-flex justify-center rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-600"
            onClick={() => {
              // Explicitly do nothing: user cannot proceed without essential consent.
              setAccepted(false);
            }}
          >
            I Do Not Agree
          </button>
        </div>

        <p className="mt-4 text-xs text-gray-400">
          If you do not agree, you will not be able to use the site.
        </p>
      </div>
    </div>
  );
};

export default CookieConsentGate;
